import { LegacyRef, useEffect, useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Modal } from '@ui/components/core'
import { DataSourceDropdown, ImportSampleEvent } from '@ui/components/elements'
import { ChevronLeftIcon, ImportIcon, PinIcon, RefreshIcon } from '@ui/components/icons'
import { DatableLog, DatableTrace } from '@ui/interfaces'
import { LayoutPanel, RIGHT_PANEL_CONTENT } from '@ui/pages/pipelinesWorkflow/components/PipelineLayoutManager'
import { usePipelineDataContext } from '@ui/providers/PipelineDataProvider'
import { useTelemetryStore } from '@ui/stores'
import { useLayoutPanelManagerStore } from '@ui/stores/pipelinesWorkflow'
import { useClickAway } from '@uidotdev/usehooks'
import clsx from 'clsx'

import { PanelContainer } from '../Containers'
import { Collapsed } from './Collapsed'
import { Expanded } from './Expanded'

export const LiveSample = () => {
  const containerRef = useRef<HTMLDivElement>(null)

  const dropdownRef = useClickAway(() => {
    setDataSourceDropdownOpen(false)
  })

  const [importSampleEventOpen, setImportSampleEventOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [originalLeftPanelWidth, setOriginalLeftPanelWidth] = useState(0)
  const [columnWidth, setColumnWidth] = useState(0)
  const [dataSourceDropdownOpen, setDataSourceDropdownOpen] = useState(false)

  const { events, uniqueKeys, loading, fetchPipelineData, refreshPipelineData } = usePipelineDataContext()

  const { setIgnoreMaxConstraints, setPanelContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      setIgnoreMaxConstraints: state.setIgnoreMaxConstraints,
      setPanelContent: state.setPanelContent,
    }))
  )

  const {
    dataSource,
    setSelectedEvent,
    setDataSource,
    setCustomSampleLog,
    setCustomSampleSpan,
    pinnedSampleEvents,
    setPinnedSampleEvents,
  } = useTelemetryStore(
    useShallow((state) => ({
      dataSource: state.dataSource,
      setSelectedEvent: state.setSelectedEvent,
      setDataSource: state.setDataSource,
      setCustomSampleLog: state.setCustomSampleLog,
      setCustomSampleSpan: state.setCustomSampleSpan,
      pinnedSampleEvents: state.pinnedSampleEvents,
      setPinnedSampleEvents: state.setPinnedSampleEvents,
    }))
  )

  const expandedPrimaryKeys = useMemo(() => {
    return dataSource === 'logs'
      ? ['resource.service.name', 'resource.host.name', 'body']
      : ['resource.service.name', 'resource.host.name', 'name']
  }, [dataSource])

  const collapsedPrimaryKeys = useMemo(() => {
    return dataSource === 'logs' ? ['resource[service.name]', 'body'] : ['resource[service.name]', 'name']
  }, [dataSource])

  useEffect(() => {
    const leftPanel = document.getElementById('left-panel')
    if (leftPanel && leftPanel.firstChild) {
      setOriginalLeftPanelWidth((leftPanel.firstChild as HTMLElement).clientWidth)
    }
  }, [])

  // Resize observer to set the column width
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      const containerWidth = entries[0].contentRect.width
      const timestampWidth = 190
      setColumnWidth(containerWidth - timestampWidth)
    })

    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [])

  const expandLiveSample = () => {
    setIgnoreMaxConstraints(LayoutPanel.LEFT_PANEL, true)
    // setPanelContent({
    //   panel: LayoutPanel.MIDDLE_PANEL,
    //   content: MIDDLE_PANEL_CONTENT.CODE_EDITOR,
    //   open: false,
    // })
    setPanelContent({
      panel: LayoutPanel.RIGHT_PANEL,
      content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
      open: false,
    })

    setIsExpanded(true)
    const leftPanel = document.getElementById('left-panel')
    if (leftPanel && leftPanel.firstChild) {
      return ((leftPanel.firstChild as HTMLElement).style.width = '100vw')
    }
  }

  const collapseLiveSample = () => {
    setIgnoreMaxConstraints(LayoutPanel.LEFT_PANEL, false)
    setIsExpanded(false)

    const leftPanel = document.getElementById('left-panel')
    if (leftPanel && leftPanel.firstChild) {
      return ((leftPanel.firstChild as HTMLElement).style.width = `${originalLeftPanelWidth}px`)
    }
  }

  const handleSaveImportedEvent = async (importedEvent: unknown) => {
    let sampleLog: DatableLog | undefined
    let sampleSpan: DatableTrace | undefined

    if (dataSource === 'logs') {
      sampleLog = importedEvent as DatableLog
      setSelectedEvent(importedEvent as DatableLog)
      setCustomSampleLog(sampleLog)
    } else {
      sampleSpan = importedEvent as DatableTrace
      setSelectedEvent(importedEvent as DatableTrace)
      setCustomSampleSpan(sampleSpan)
    }

    try {
      fetchPipelineData(sampleLog || sampleSpan)
    } catch (error) {
      console.error(error)
    }
    setImportSampleEventOpen(false)
  }

  return (
    <PanelContainer
      title="Live Sample"
      loading={false}
      ref={containerRef}
      panelActionElement={
        <div className="w-fit">
          <DataSourceDropdown
            ref={dropdownRef as LegacyRef<HTMLDivElement>}
            dataSource={dataSource}
            setDataSource={setDataSource}
            dataSourceDropdownOpen={dataSourceDropdownOpen}
            setDataSourceDropdownOpen={setDataSourceDropdownOpen}
          />
        </div>
      }>
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-row items-center justify-end space-x-1 px-4 py-2">
          <RefreshIcon
            className="size-5 cursor-pointer fill-none stroke-gray-900 stroke-[1.6] transition-all duration-300 hover:stroke-primary-500"
            onClick={refreshPipelineData}
          />
          <ImportIcon
            className="size-5 cursor-pointer text-gray-900 transition-all duration-300 hover:stroke-primary-500 active:scale-90"
            strokeWidth={1.6}
            onClick={() => setImportSampleEventOpen(true)}
          />
          <PinIcon
            className={clsx(
              'size-5 cursor-pointer stroke-gray-900 stroke-[1.6] transition-all duration-300 hover:stroke-primary-500 active:scale-90',
              {
                'fill-primary-500': pinnedSampleEvents,
                'fill-none': !pinnedSampleEvents,
              }
            )}
            onClick={() => setPinnedSampleEvents(!pinnedSampleEvents)}
          />
          <ChevronLeftIcon
            className={clsx(
              'size-6 cursor-pointer stroke-gray-900 stroke-2 transition-all duration-300 hover:stroke-primary-500 active:scale-90',
              {
                'rotate-180': !isExpanded,
              }
            )}
            onClick={() => (isExpanded ? collapseLiveSample() : expandLiveSample())}
          />
        </div>

        {pinnedSampleEvents && (
          <div className="flex w-full items-center justify-center bg-primary-500 p-2">
            <p className="text-sm font-medium text-white">Pinned</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={importSampleEventOpen}
        onClose={() => setImportSampleEventOpen(false)}
        title={`Import sample ${dataSource.substring(0, dataSource.length - 1)}`}>
        <ImportSampleEvent handleSave={handleSaveImportedEvent} loading={loading} />
      </Modal>

      {isExpanded ? (
        <Expanded
          events={events as { input: DatableLog; output: DatableLog }[]}
          isLoading={loading}
          dataSource={dataSource}
          primaryKeys={expandedPrimaryKeys}
        />
      ) : (
        <Collapsed
          events={events as { input: DatableLog; output: DatableLog }[]}
          isLoading={loading}
          dataSource={dataSource}
          columnWidth={columnWidth}
          primaryKeys={collapsedPrimaryKeys}
          uniqueKeys={uniqueKeys}
        />
      )}
    </PanelContainer>
  )
}
